// OCR helper — extracts OPTCG card numbers from a tight crop around the
// card's bottom-right corner.
//
// Pipeline:
//   1. Crop x ∈ [55%, 100%], y ∈ [65%, 100%] at NATURAL resolution
//      (no downscale — every pixel per glyph matters for tesseract).
//   2. Plain grayscale. No contrast stretch — empirically it washed out
//      text on foil cards.
//   3. Tesseract with PSM 11 (sparse text). The crop is small and tight,
//      but may still contain stray rules-box bleed.
//   4. Try strict card-number regex first; if it misses, scan candidate
//      tokens and substitute the classic OCR confusions (O→0, I→1, L→1,
//      B→8, S→5) in the trailing digit portion only — set codes stay
//      letters, card-number digits get rescued.

import { createWorker, PSM, type Worker } from 'tesseract.js'

let workerPromise: Promise<Worker> | null = null

async function getWorker(): Promise<Worker> {
  if (workerPromise) return workerPromise
  workerPromise = (async () => {
    const w = await createWorker('eng')
    await w.setParameters({
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-',
      tessedit_pageseg_mode: PSM.SPARSE_TEXT,
    })
    return w
  })()
  return workerPromise
}

const STRICT_RE = /\b[A-Z]{1,4}\d{1,3}(?:-\d{1,4})?\b/g
// Token shape: a letter followed by a mix of letters/digits, possibly with
// a hyphen + more letters/digits. Used to find OCR fragments that might
// be card numbers after letter→digit normalisation.
const LOOSE_TOKEN_RE = /\b[A-Z][A-Z0-9]{1,7}(?:-[A-Z0-9]{1,4})?\b/g

export type OcrResult = {
  raw: string
  numbers: string[]
  crop_data_url: string
}

export async function extractCardNumbers(file: File): Promise<OcrResult> {
  const { canvas, dataUrl } = await preparedCrop(file)
  const worker = await getWorker()
  const blob = await new Promise<Blob>((r, j) =>
    canvas.toBlob((b) => (b ? r(b) : j(new Error('toBlob'))), 'image/jpeg', 0.95),
  )
  const { data: { text } } = await worker.recognize(blob)
  return {
    raw: text,
    numbers: extractWithFallback(text),
    crop_data_url: dataUrl,
  }
}

/** Strict match first; then a fallback that fixes O→0 / I→1 confusions. */
function extractWithFallback(text: string): string[] {
  const T = text.toUpperCase()
  const out = new Set<string>()
  for (const m of T.match(STRICT_RE) ?? []) out.add(m)
  for (const token of T.match(LOOSE_TOKEN_RE) ?? []) {
    const normalised = rescueDigits(token)
    if (normalised && STRICT_RE.test(normalised)) {
      out.add(normalised)
      STRICT_RE.lastIndex = 0 // RegExp .test() leaves lastIndex stateful
    }
  }
  return Array.from(out)
}

/**
 * Split a token into (letter prefix, rest). Substitute the canonical OCR
 * letter→digit confusions in the rest part only — prefix stays as letters
 * (OPTCG set codes are always letters).
 */
function rescueDigits(token: string): string | null {
  const firstDigit = token.search(/[0-9]/)
  if (firstDigit <= 0 || firstDigit > 4) return null
  const prefix = token.slice(0, firstDigit)
  const rest = token.slice(firstDigit)
    .replace(/O/g, '0')
    .replace(/I/g, '1')
    .replace(/L/g, '1')
    .replace(/B/g, '8')
    .replace(/S/g, '5')
  return prefix + rest
}

// ─────────────────────────────────────────────────────────────────────────
// Image preparation: tight bottom-right crop + grayscale.

async function preparedCrop(file: File): Promise<{ canvas: HTMLCanvasElement; dataUrl: string }> {
  const img = await loadImage(file)
  const W = img.naturalWidth
  const H = img.naturalHeight
  const cropX = Math.round(W * 0.55)
  const cropY = Math.round(H * 0.65)
  const cropW = W - cropX
  const cropH = H - cropY
  const canvas = document.createElement('canvas')
  canvas.width = cropW
  canvas.height = cropH
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false  // no smoothing — preserve glyph crispness
  ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH)
  const id = ctx.getImageData(0, 0, cropW, cropH)
  toGrayInPlace(id.data)
  ctx.putImageData(id, 0, 0)
  return { canvas, dataUrl: canvas.toDataURL('image/png') }
}

function toGrayInPlace(data: Uint8ClampedArray) {
  for (let i = 0; i < data.length; i += 4) {
    const y = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29 + 128) >>> 8
    data[i] = y; data[i + 1] = y; data[i + 2] = y
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')) }
    img.src = url
  })
}
